import photoService, { PositionType, PhotoType, PhotoUpdateCallback, PhotoSource } from './PhotoService';
import { base64ToFile, FILE_UPLOAD_FAILED_KEY } from '../../../services/DeliveryDataService';
import { fileUpload } from '../../../utils/storageUtils';
import { Toast } from 'antd-mobile';
import { t } from '../../../i18n';
import { GeoPosition } from './GeolocationService';

export interface MultiPhotoResult {
  success: boolean;
  photos: Record<PositionType, PhotoType | undefined>;
  errors?: string[];
}

// 新版本数组形式的回调类型
export type PhotoArrayUpdateCallback = (updateFn: (prev: PhotoType[]) => PhotoType[]) => void;

export interface MultiPhotoArrayResult {
  success: boolean;
  photos: PhotoType[];
  errors?: string[];
}

class MultiPhotoService {
  /**
   * 处理多图片选择 - 立即展示 + 异步上传
   * @param base64Photos base64图片数组
   * @param onPhotosUpdate 照片更新回调
   * @returns Promise<MultiPhotoResult>
   */
  async handleMultiPhotoSelection(
    base64Photos: string[],
    onPhotosUpdate: PhotoUpdateCallback
  ): Promise<MultiPhotoResult> {
    const positions: PositionType[] = ['label', 'location', 'houseNumber'];
    const errors: string[] = [];
    
    // 第一步：立即展示所有base64图片
    this.immediateDisplay(base64Photos, onPhotosUpdate);
    
    // 第二步：异步上传所有图片
    const uploadPromises = base64Photos.map(async (photo, index) => {
      if (photo && index < positions.length) {
        try {
          return await this.uploadSinglePhoto(photo, positions[index], onPhotosUpdate);
        } catch (error) {
          const errorMsg = `Failed to upload ${positions[index]} photo: ${error}`;
          errors.push(errorMsg);
          console.error(errorMsg);
          return false;
        }
      }
      return true;
    });
    
    await Promise.all(uploadPromises);
    
    return {
      success: errors.length === 0,
      photos: {} as Record<PositionType, PhotoType | undefined>, // 这里可以返回最终状态
      errors
    };
  }
  
  /**
   * 立即展示base64图片
   */
  private immediateDisplay(base64Photos: string[], onPhotosUpdate: PhotoUpdateCallback): void {
    onPhotosUpdate(prev => {
      const updated = { ...prev };
      const positions: PositionType[] = ['label', 'location', 'houseNumber'];
      
      base64Photos.forEach((photo, index) => {
        if (photo && index < positions.length) {
          // 确保base64格式正确用于显示
          const formattedPhoto = this.ensureBase64Format(photo);
          updated[positions[index]] = {
            url: formattedPhoto,
            objectKey: '' // 先留空，等待上传
          };
        }
      });
      
      return updated;
    });
  }
  
  /**
   * 上传单张图片
   */
  private async uploadSinglePhoto(
    base64Photo: string, 
    position: PositionType, 
    onPhotosUpdate: PhotoUpdateCallback
  ): Promise<boolean> {
    try {
      // 确保base64格式正确（添加data URL前缀如果不存在）
      const formattedBase64 = this.ensureBase64Format(base64Photo);
      
      // 转换为File对象
      const file = await base64ToFile(formattedBase64, `gallery_${position}_${Date.now()}.jpg`);
      
      // 上传文件
      const result = await fileUpload(file, `gallery_${position}`);
      
      // 更新objectKey
      onPhotosUpdate(prev => {
        const prevPhoto = prev[position];
        return {
          ...prev,
          [position]: {
            url: prevPhoto?.url || base64Photo,
            objectKey: result.objectKey
          }
        };
      });
      
      return true;
    } catch (error) {
      // 上传失败，标记为失败状态
      onPhotosUpdate(prev => {
        const prevPhoto = prev[position];
        return {
          ...prev,
          [position]: {
            url: prevPhoto?.url || base64Photo,
            objectKey: FILE_UPLOAD_FAILED_KEY
          }
        };
      });
      
      throw error;
    }
  }
  
  /**
   * 确保base64格式正确，添加data URL前缀如果不存在
   */
  private ensureBase64Format(base64Data: string): string {
    // 如果已经是data URL格式，直接返回
    if (base64Data.startsWith('data:')) {
      return base64Data;
    }
    
    // 如果不是data URL格式，添加前缀
    return `data:image/jpeg;base64,${base64Data}`;
  }
  
  /**
   * 批量删除图片
   */
  async handleMultiPhotoDelete(
    positions: PositionType[],
    onPhotosUpdate: PhotoUpdateCallback
  ): Promise<void> {
    positions.forEach(position => {
      photoService.handleDeletePhoto(position, onPhotosUpdate);
    });
  }

  // ===== 新版本数组形式的方法 =====

  /**
   * 处理多图片选择 - 数组版本（按照老版本逻辑：不立即上传）
   * @param base64Photos base64图片数组
   * @param onPhotosUpdate 照片更新回调
   * @param position 当前经纬度信息
   * @param source 照片来源（拍照 or 相册）
   * @returns Promise<MultiPhotoArrayResult>
   */
  async handleMultiPhotoSelectionArray(
    base64Photos: string[],
    onPhotosUpdate: PhotoArrayUpdateCallback,
    position?: GeoPosition | null,
    source: PhotoSource = 'camera' // 默认为拍照
  ): Promise<MultiPhotoArrayResult> {
    try {
      // 按照老版本逻辑：只转换为base64存储，不立即上传
      this.immediateDisplayArrayUnUpload(base64Photos, onPhotosUpdate, position, source);
      
      return {
        success: true,
        photos: [], // 返回空数组，因为没有上传
        errors: []
      };
    } catch (error) {
      console.error('Failed to handle multi photo selection:', error);
      return {
        success: false,
        photos: [],
        errors: [`Failed to handle photos: ${error}`]
      };
    }
  }


async handleMultiPhotoSelectionArrayWithBatched(
  base64Photos: string[],
  onPhotosUpdate: PhotoArrayUpdateCallback,
  position?: GeoPosition | null,
  source: PhotoSource = 'camera' // 默认为拍照
): Promise<MultiPhotoArrayResult> {
  try {    
    // 关键修改：调用异步分批方法，并使用 await 等待完成
    await this.immediateDisplayArrayUnUploadBatched(base64Photos, onPhotosUpdate, undefined, position, source);
    //console.log('调用异步分批方法，并使用 await 等待完成---------------');  
    return {      
      success: true,
      photos: [],
      errors: []
    };
  } catch (error) {
    console.error('Failed to handle multi photo selection:', error);
    return {
      success: false,
      photos: [],
      errors: [`Failed to handle photos: ${error}`]
    };
  }
}

  /**
   * 立即展示base64图片 - 数组版本（按照老版本逻辑：不上传）
   */
  private immediateDisplayArrayUnUpload(
    base64Photos: string[], 
    onPhotosUpdate: PhotoArrayUpdateCallback, 
    position?: GeoPosition | null,
    source: PhotoSource = 'camera' // 默认为拍照
  ): void {
    onPhotosUpdate(prev => {
      const updated = [...prev];
      
      base64Photos.forEach((photo, index) => {
        if (photo) {
          // 确保base64格式正确用于显示
          const formattedPhoto = this.ensureBase64Format(photo);
          updated.push({
            url: formattedPhoto,
            objectKey: FILE_UPLOAD_FAILED_KEY, // 按照老版本逻辑，设置为未上传状态
            latitude: position?.latitude,
            longitude: position?.longitude,
            capturedAt: Date.now(),
            source: source
          });
        }
      });
      
      return updated;
    });
  }

  /**
 * 立即展示base64图片 - 数组版本（分批异步处理，避免卡死）
 * @param base64Photos base64图片数组
 * @param onPhotosUpdate 照片更新回调
 * @param batchDelay 每批之间的延迟（毫秒），默认 300ms
 */
private async immediateDisplayArrayUnUploadBatched(
  base64Photos: string[], 
  onPhotosUpdate: PhotoArrayUpdateCallback,
  batchDelay: number = 300,
  position?: GeoPosition | null,
  source: PhotoSource = 'camera' // 默认为拍照
): Promise<void> {
  //console.log('📸 开始分批处理照片，总数:', base64Photos.length);
  
  // 逐张添加照片
  for (let i = 0; i < base64Photos.length; i++) {
    const photo = base64Photos[i];
    
    if (photo) {
      const startTime = performance.now();
      
      // 确保base64格式正确用于显示
      const formattedPhoto = this.ensureBase64Format(photo);
      
      //console.log(`📸 处理第 ${i + 1}/${base64Photos.length} 张照片`);
      
      // 添加单张照片到状态
      onPhotosUpdate(prev => {
        const updated = [...prev];
        updated.push({
          url: formattedPhoto,
          objectKey: FILE_UPLOAD_FAILED_KEY,
          latitude: position?.latitude,
          longitude: position?.longitude,
          capturedAt: Date.now(),
          source: source
        });
        return updated;
      });
      
      const endTime = performance.now();
      //console.log(`⏰ 第 ${i + 1} 张照片处理耗时: ${(endTime - startTime).toFixed(2)}ms`);
      
      // 如果不是最后一张，等待一段时间让浏览器有机会解码
      // 这样可以避免同时解码多张图片导致内存峰值过高
      if (i < base64Photos.length - 1) {
        await new Promise(resolve => setTimeout(resolve, batchDelay));
      }
    }
  }
  
  //console.log('✅ 所有照片处理完成');
}

  /**
   * 立即展示base64图片 - 数组版本（旧方法，保留用于其他地方）
   */
  private immediateDisplayArray(base64Photos: string[], onPhotosUpdate: PhotoArrayUpdateCallback): void {
    onPhotosUpdate(prev => {
      const updated = [...prev];
      
      base64Photos.forEach((photo, index) => {
        if (photo) {
          // 确保base64格式正确用于显示
          const formattedPhoto = this.ensureBase64Format(photo);
          updated.push({
            url: formattedPhoto,
            objectKey: '' // 先留空，等待上传
          });
        }
      });
      
      return updated;
    });
  }

  /**
   * 上传单张图片 - 数组版本
   */
  private async uploadSinglePhotoArray(
    base64Photo: string, 
    index: number, 
    onPhotosUpdate: PhotoArrayUpdateCallback
  ): Promise<boolean> {
    try {
      // 确保base64格式正确（添加data URL前缀如果不存在）
      const formattedBase64 = this.ensureBase64Format(base64Photo);
      
      // 转换为File对象
      const file = await base64ToFile(formattedBase64, `gallery_${index}_${Date.now()}.jpg`);
      
      // 上传文件
      const result = await fileUpload(file, `gallery_${index}`);
      
      // 更新objectKey
      onPhotosUpdate(prev => {
        const updated = [...prev];
        if (updated[index]) {
          updated[index] = {
            ...updated[index],
            objectKey: result.objectKey
          };
        }
        return updated;
      });
      
      return true;
    } catch (error) {
      // 上传失败，标记为失败状态
      onPhotosUpdate(prev => {
        const updated = [...prev];
        if (updated[index]) {
          updated[index] = {
            ...updated[index],
            objectKey: FILE_UPLOAD_FAILED_KEY
          };
        }
        return updated;
      });
      
      throw error;
    }
  }

  /**
   * 处理拍照或选择照片 - 数组版本（按照老版本逻辑：不立即上传）
   */
  async handlePhotoUnUploadArray(
    file: File,
    onPhotosUpdate: PhotoArrayUpdateCallback,
    position?: GeoPosition | null,
    source: PhotoSource = 'camera' // 默认为拍照
  ): Promise<void> {
    try {
      // 使用提取的方法将文件转换为base64格式
      const base64Data = await photoService.fileToBase64(file);

      onPhotosUpdate(prev => {
        const updated = [...prev];
        updated.push({
          url: base64Data,
          objectKey: FILE_UPLOAD_FAILED_KEY, // 按照老版本逻辑，设置为未上传状态
          latitude: position?.latitude,
          longitude: position?.longitude,
          capturedAt: Date.now(),
          source: source
        });
        return updated;
      });

    } catch (error) {
      console.error('Failed to take photo:', error);
      Toast.show({ content: `Failed to take photo, please try again`, position: 'bottom' });
      throw error;
    }
  }

  /**
   * 处理删除照片 - 数组版本
   */
  async handleDeletePhotoArray(
    index: number,
    onPhotosUpdate: PhotoArrayUpdateCallback
  ): Promise<void> {
    onPhotosUpdate(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
    Toast.show({ content: t('pages.delivery.detail.photo.deleted'), position: 'bottom' });
  }

  /**
   * 处理多图片选择 - 每张照片带独立经纬度（连拍专用）
   * @param photosWithPosition 照片数组，每张照片带自己的经纬度
   * @param onPhotosUpdate 照片更新回调
   * @param source 照片来源（拍照 or 相册）
   * @returns Promise<MultiPhotoArrayResult>
   */
  async handleMultiPhotoSelectionArrayWithIndividualPositions(
    photosWithPosition: Array<{ photo: string; position: GeoPosition | null }>,
    onPhotosUpdate: PhotoArrayUpdateCallback,
    source: PhotoSource = 'camera'
  ): Promise<MultiPhotoArrayResult> {
    try {
      onPhotosUpdate(prev => {
        const updated = [...prev];
        
        photosWithPosition.forEach(({ photo, position }) => {
          if (photo) {
            // 确保base64格式正确用于显示
            const formattedPhoto = this.ensureBase64Format(photo);
            updated.push({
              url: formattedPhoto,
              objectKey: FILE_UPLOAD_FAILED_KEY, // 按照老版本逻辑，设置为未上传状态
              latitude: position?.latitude,
              longitude: position?.longitude,
              capturedAt: Date.now(),
              source: source
            });
          }
        });
        
        return updated;
      });
      
      return {
        success: true,
        photos: [],
        errors: []
      };
    } catch (error) {
      console.error('Failed to handle multi photo selection with individual positions:', error);
      return {
        success: false,
        photos: [],
        errors: [`Failed to handle photos: ${error}`]
      };
    }
  }

 /**
   * 处理多图片选择 - 每张照片带独立经纬度（连拍专用）
   * @param photosWithPosition 照片数组，每张照片带自己的经纬度
   * @param onPhotosUpdate 照片更新回调
   * @param source 照片来源（拍照 or 相册）
   * @returns Promise<MultiPhotoArrayResult>
   */
  async handleMultiPhotoSelectionArrayWithIndividualPositionsBatched(
    photosWithPosition: Array<{ photo: string; position: GeoPosition | null }>,
    onPhotosUpdate: PhotoArrayUpdateCallback,
    source: PhotoSource = 'camera'
  ): Promise<MultiPhotoArrayResult> {
    try {
      // console.log(`📸 开始处理照片${photosWithPosition.length} 张照片`);
      await this.immediateDisplayArrayUnUploadBatchedForCamera(photosWithPosition, onPhotosUpdate, 300, source);
      
      return {
        success: true,
        photos: [],
        errors: []
      };
    } catch (error) {
      console.error('Failed to handle multi photo selection with individual positions:', error);
      return {
        success: false,
        photos: [],
        errors: [`Failed to handle photos: ${error}`]
      };
    }
  }

    /**
 * 分批异步处理，避免卡死
 * @param photosWithPosition 照片数组，每张照片带自己的经纬度
 * @param onPhotosUpdate 照片更新回调
 * @param batchDelay 每批之间的延迟（毫秒），默认 300ms
 * @param source 照片来源（拍照 or 相册）
 */
private async immediateDisplayArrayUnUploadBatchedForCamera(
  photosWithPosition: Array<{ photo: string; position: GeoPosition | null }>, 
  onPhotosUpdate: PhotoArrayUpdateCallback,
  batchDelay: number = 300,
  source: PhotoSource = 'camera' // 默认为拍照
): Promise<void> {
  //console.log('📸 开始分批处理照片，总数:', base64Photos.length);
  
  // 逐张添加照片
  for (let i = 0; i < photosWithPosition.length; i++) {
    const photo = photosWithPosition[i]["photo"];
    const position = photosWithPosition[i]["position"];
    
    if (photo) {
      const startTime = performance.now();
      
      // 确保base64格式正确用于显示
      const formattedPhoto = this.ensureBase64Format(photo);
      
      // console.log(`📸 处理第 ${i + 1}/${photosWithPosition.length} 张照片`);
      
      // 添加单张照片到状态
      onPhotosUpdate(prev => {
        const updated = [...prev];
        updated.push({
          url: formattedPhoto,
          objectKey: FILE_UPLOAD_FAILED_KEY,
          latitude: position?.latitude,
          longitude: position?.longitude,
          capturedAt: Date.now(),
          source: source
        });
        return updated;
      });
      
      const endTime = performance.now();
      //console.log(`⏰ 第 ${i + 1} 张照片处理耗时: ${(endTime - startTime).toFixed(2)}ms`);
      
      // 如果不是最后一张，等待一段时间让浏览器有机会解码
      // 这样可以避免同时解码多张图片导致内存峰值过高
      if (i < photosWithPosition.length - 1) {
        await new Promise(resolve => setTimeout(resolve, batchDelay));
      }
    }
  }
  
  //console.log('✅ 所有照片处理完成');
}


}

const multiPhotoService = new MultiPhotoService();
export default multiPhotoService;
